import { NON_BREAKABLE_SPACE } from "constants/common";
import { format } from "date-fns";
import { startCase } from "lodash";
import type { Appointment } from "services/types/appointments.type";
import { convertDateInTimezoneToCurrent } from "utils/common";

import { RRule, Weekday, Frequency, Options as RRuleOptions } from "rrule";
import { merge } from "lodash";
import EnumDataSource from "utils/enum-data-source.util";

export const RECURRENCE_TYPE_DAILY = 1;
export const RECURRENCE_TYPE_WEEKLY = 2;
export const RECURRENCE_TYPE_MONTHLY = 3;
export const RECURRENCE_TYPE_YEARLY = 4;

const recurrenceTypes = {
  [RECURRENCE_TYPE_DAILY]: "Daily",
  [RECURRENCE_TYPE_WEEKLY]: "Weekly",
  [RECURRENCE_TYPE_MONTHLY]: "Monthly",
  [RECURRENCE_TYPE_YEARLY]: "Yearly",
};

export const RECURRENCE_TYPES = new EnumDataSource(recurrenceTypes);

export enum WeekDay {
  MO = "MO",
  TU = "TU",
  WE = "WE",
  TH = "TH",
  FR = "FR",
  SA = "SA",
  SU = "SU",
}

export enum MonthOption {
  BY_DAY = "byDay",
  BY_MONTH_DAY = "byMonthDay",
  BY_SET_POS = "bySetPos",
  LAST_DAY = "lastDay",
}

export type MonthDay = number; // 1-31 or negative for last days
export type BySetPos = number; // 1-4, -1 for last

export const WEEKDAY_ORDER: WeekDay[] = [
  WeekDay.MO,
  WeekDay.TU,
  WeekDay.WE,
  WeekDay.TH,
  WeekDay.FR,
  WeekDay.SA,
  WeekDay.SU,
];

export const WEEKDAY_LABELS: Record<WeekDay, string> = {
  [WeekDay.MO]: "Monday",
  [WeekDay.TU]: "Tuesday",
  [WeekDay.WE]: "Wednesday",
  [WeekDay.TH]: "Thursday",
  [WeekDay.FR]: "Friday",
  [WeekDay.SA]: "Saturday",
  [WeekDay.SU]: "Sunday",
};

export const WEEK_DAY_NUMBER_FIRST = 1;
export const WEEK_DAY_NUMBER_SECOND = 2;
export const WEEK_DAY_NUMBER_THIRD = 3;
export const WEEK_DAY_NUMBER_FOURTH = 4;
export const WEEK_DAY_NUMBER_LAST = -1;

export const WEEK_DAY_NUMBERS = new EnumDataSource({
  [WEEK_DAY_NUMBER_FIRST]: "First",
  [WEEK_DAY_NUMBER_SECOND]: "Second",
  [WEEK_DAY_NUMBER_THIRD]: "Third",
  [WEEK_DAY_NUMBER_FOURTH]: "Fourth",
  [WEEK_DAY_NUMBER_LAST]: "Last",
});

export function toArray<T>(val: T | T[] | undefined): T[] {
  if (val === undefined) return [];
  return Array.isArray(val) ? val : [val];
}

export function rruleWeekdayToString(weekday: Weekday): WeekDay {
  return WEEKDAY_ORDER[weekday.weekday];
}

export const toInternalFrequency = (rruleFreq: number): number => {
  if (rruleFreq === Frequency.YEARLY) return RECURRENCE_TYPE_YEARLY;
  if (rruleFreq === Frequency.MONTHLY) return RECURRENCE_TYPE_MONTHLY;
  if (rruleFreq === Frequency.WEEKLY) return RECURRENCE_TYPE_WEEKLY;
  if (rruleFreq === Frequency.DAILY) return RECURRENCE_TYPE_DAILY;
  return RECURRENCE_TYPE_WEEKLY;
};

export const toRRuleFrequency = (internalFreq: number): number => {
  switch (internalFreq) {
    case RECURRENCE_TYPE_DAILY:
      return Frequency.DAILY;
    case RECURRENCE_TYPE_WEEKLY:
      return Frequency.WEEKLY;
    case RECURRENCE_TYPE_MONTHLY:
      return Frequency.MONTHLY;
    case RECURRENCE_TYPE_YEARLY:
      return Frequency.YEARLY;
    default:
      return Frequency.WEEKLY;
  }
};

export interface RRuleResult {
  rrule?: string;
  text?: string;
}

export const parseValues = (
  state: RecurrenceStateValues,
  shouldThrow = false
): RRuleResult => {
  try {
    const options: Partial<RRuleOptions> = {
      freq: toRRuleFrequency(Number(state.frequency)),
      interval: Math.max(1, state.interval || 1),
    };

    if (
      Number(state.frequency) === RECURRENCE_TYPE_WEEKLY &&
      state.weekDays.length > 0
    ) {
      options.byweekday = state.weekDays
        .filter(Boolean)
        .map((day) => RRule[day]);
    } else if (Number(state.frequency) === RECURRENCE_TYPE_MONTHLY) {
      switch (state.monthOption) {
        case MonthOption.BY_MONTH_DAY:
          options.bymonthday = state.monthDays;
          break;
        case MonthOption.BY_SET_POS:
          if (state.monthWeekDay) {
            const day = state.monthWeekDay.day || WeekDay.MO;

            options.bysetpos = [state.monthWeekDay.pos || 1];
            options.byweekday = [RRule[day]];
          }
          break;
        case MonthOption.LAST_DAY:
          options.bymonthday = [-1];
          break;
        case MonthOption.BY_DAY:
        default:
          break;
      }
    }

    const rule = new RRule(options as RRuleOptions);

    let text: string;

    if (
      Number(state.frequency) === RECURRENCE_TYPE_MONTHLY &&
      state.monthOption === MonthOption.BY_SET_POS &&
      state.monthWeekDay
    ) {
      const positionText = getPositionText(state.monthWeekDay.pos);
      const dayText =
        WEEKDAY_LABELS[state.monthWeekDay.day] || state.monthWeekDay.day;
      const intervalText =
        state.interval === 1 ? "month" : `${state.interval} months`;
      text = `every ${intervalText} on the ${positionText} ${dayText}`;
    } else {
      text = rule.toText();
    }

    return {
      rrule: rule.toString(),
      text,
    };
  } catch (e) {
    console.error("Error generating RRULE:", e, state);
    if (shouldThrow) {
      throw e;
    }
    return {};
  }
};

const DEFAULT_VALUES: RecurrenceStateValues = {
  frequency: RECURRENCE_TYPE_WEEKLY,
  interval: 1,
  count: null,
  weekDays: [],
  monthOption: MonthOption.BY_MONTH_DAY,
  monthDays: null,
  monthWeekDay: null,
};

export const getInitializedValues = (rule: RRule): RecurrenceStateValues => {
  const options = rule.origOptions;

  const newState: RecurrenceStateValues = {
    ...DEFAULT_VALUES,
    frequency:
      options.freq !== undefined
        ? toInternalFrequency(options.freq)
        : RECURRENCE_TYPE_WEEKLY,
    interval: options.interval || 1,
    count: options.count || null,
    weekDays: [],
    monthOption: MonthOption.BY_MONTH_DAY,
    monthDays: null,
    monthWeekDay: null,
  };

  if (options.byweekday) {
    const byweekdayArr = toArray(options.byweekday) as Weekday[];
    newState.weekDays = byweekdayArr.map(rruleWeekdayToString);
  }

  if (toArray(options.bymonthday || []).includes(-1)) {
    newState.monthOption = MonthOption.LAST_DAY;
  } else if (options.bymonthday) {
    newState.monthOption = MonthOption.BY_MONTH_DAY;
    newState.monthDays = options.bymonthday as number;
  } else if (options.bysetpos && options.byweekday) {
    newState.monthOption = MonthOption.BY_SET_POS;
    const byweekdayArr = toArray(options.byweekday) as Weekday[];
    newState.monthWeekDay = byweekdayArr.length
      ? {
          day: rruleWeekdayToString(byweekdayArr[0]),
          pos: toArray(options.bysetpos)[0],
        }
      : null;
  } else {
    newState.monthOption = MonthOption.BY_DAY;
  }

  return merge({}, DEFAULT_VALUES, newState);
};

const getPositionText = (pos: number): string => {
  switch (pos) {
    case 1:
      return "first";
    case 2:
      return "second";
    case 3:
      return "third";
    case 4:
      return "fourth";
    case -1:
      return "last";
    default:
      return "";
  }
};

export interface RecurrenceStateValues {
  frequency: number;
  interval: number;
  count: number | null;
  weekDays: WeekDay[];
  monthOption: MonthOption;
  monthDays: MonthDay | null;
  monthWeekDay: { day: WeekDay; pos: BySetPos } | null;
}

export const getRRuleText = (rrule?: string) => {
  if (!rrule) return "";

  try {
    const rule = RRule.fromString(rrule);
    return parseValues(getInitializedValues(rule)).text;
  } catch (error) {
    console.error("Error parsing RRULE:", error);
  }
};

export const convertLegacyRecurrenceToRRULE = (
  recurrence_period: string | null | undefined,
  recurrence_day: number | string | null | undefined,
  recurrence_week: number | string | null | undefined
): string => {
  if (!recurrence_period) {
    return "";
  }

  const day =
    typeof recurrence_day === "string"
      ? parseInt(recurrence_day)
      : recurrence_day;
  const week =
    typeof recurrence_week === "string"
      ? parseInt(recurrence_week)
      : recurrence_week;

  const options: Partial<any> = {
    freq: RRule.DAILY,
  };

  const weekdays = [
    RRule.MO,
    RRule.TU,
    RRule.WE,
    RRule.TH,
    RRule.FR,
    RRule.SA,
    RRule.SU,
  ];

  switch (recurrence_period) {
    case "D":
      options.freq = Frequency.DAILY;
      break;

    case "W":
      options.freq = Frequency.WEEKLY;
      if (day !== null && day !== undefined && !isNaN(day)) {
        options.byweekday = [weekdays[day - 1]];
      }
      break;

    case "M":
      options.freq = Frequency.MONTHLY;
      if (day !== null && day !== undefined && !isNaN(day)) {
        options.bymonthday = [day];
      }
      break;

    case "DWM":
      options.freq = Frequency.MONTHLY;
      if (
        day !== null &&
        day !== undefined &&
        !isNaN(day) &&
        week !== null &&
        week !== undefined &&
        !isNaN(week)
      ) {
        const weekday = weekdays[day - 1];

        const pos = week === 5 ? -1 : week;

        options.byweekday = [weekday];
        options.bysetpos = [pos];
      }
      break;

    default:
      return "";
  }

  try {
    const rule = new RRule(options);
    return rule.toString();
  } catch (e) {
    console.error("Error generating RRULE:", e);
    return "";
  }
};

export const isRecurrenceAppointment = (appointment: Appointment) => {
  if (!appointment?.schedule) return false;
  const { rrule, recurrence_period, recurrence_day, recurrence_week } =
    appointment.schedule;

  return [rrule, recurrence_period, recurrence_day, recurrence_week]
    .filter(Boolean)
    .some((value) => value);
};

export const getRecurrenceTextDetails = (params: {
  appointment: Appointment;
  startDate?: string | null;
  startTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
  timezone?: string | null;
}) => {
  const { appointment, startDate, startTime, endDate, endTime, timezone } =
    params;
  if (!appointment?.schedule) return "";

  const { rrule, recurrence_period, recurrence_day, recurrence_week } =
    appointment.schedule;

  const text = startCase(
    getRRuleText(
      rrule
        ? rrule
        : convertLegacyRecurrenceToRRULE(
            recurrence_period,
            recurrence_day,
            recurrence_week
          )
    )
  );

  const startMoment = [startDate, startTime].filter(Boolean).join(" ");
  const startMomentDate = startMoment
    ? convertDateInTimezoneToCurrent(startMoment, timezone || undefined)
    : undefined;

  const endMoment = [endDate, endTime].filter(Boolean).join(" ");
  const endMomentDate = endMoment
    ? convertDateInTimezoneToCurrent(endMoment, timezone || undefined)
    : undefined;

  if (!startMomentDate) {
    return `Occurs ${text}`;
  }

  if (!endMomentDate) {
    return `Occurs ${text} from${NON_BREAKABLE_SPACE}${format(
      startMomentDate,
      "EEE MMM dd yyyy"
    )
      .split(" ")
      .join(NON_BREAKABLE_SPACE)}`;
  }

  return `Occurs ${text} from${NON_BREAKABLE_SPACE}${format(
    startMomentDate,
    "EEE MMM dd yyyy"
  )
    .split(" ")
    .join(NON_BREAKABLE_SPACE)}, to${NON_BREAKABLE_SPACE}${format(
    endMomentDate,
    "EEE MMM dd yyyy"
  )
    .split(" ")
    .join(NON_BREAKABLE_SPACE)}`;
};
